import Paper from "@mui/material/Paper";
import React, {CSSProperties, useCallback, useEffect, useMemo, useState} from "react";
import {Day} from "../core/Day";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import {IBoard} from "../core/IBoard";
import {ClickAwayListener, Input, SxProps} from "@mui/material";
import {deleteBoardById, getBoardById, saveBoard} from "../helper_functions";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import Button from "@mui/material/Button";
import {useNavigate} from "react-router";
import DayPopup from "../components/DayPopup";
import {Option} from "../core/Option";
import AddIcon from '@mui/icons-material/Add';
import AddOptionPopup from "../components/AddOptionPopup";
import EditOptionPopup from "../components/EditOptionPopup";
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteBoardPopup from "../components/DeleteBoardPopup";
import {enqueueSnackbar} from "notistack";
import { AgCharts } from "ag-charts-react";

interface IProps extends IBoard{

    selectedYear: number;
    onClick?: () => void;
    options?: {
        sx?: SxProps;
    }
    reloadBoard?: () => void;

}

export default function SelectedBoard(props: IProps){

    const { id, title: startTitle, data, availableOptions, onClick, selectedYear, options, reloadBoard } = props;

    const [title, setTitle] = useState(startTitle);
    const navigate = useNavigate();

    const [selectedDay, setSelectedDay] = useState<{day: number; month: number; dayObject: Day}|null>(null);
    const [openNewOptionPopup, setOpenNewOptionPopup] = useState<boolean>(false);
    const [showBoardDeleteConfirmationDialog, setShowBoardDeleteConfirmationDialog] = useState<boolean>(false);
    const [modifyingOption, setModifyingOption] = useState<Option|null>(null);

    const handleOptionAdd = (option: Option) => {
        saveBoard({
            title: title,
            data: data,
            id: id,
            availableOptions: [ ...availableOptions, option]
        });
        availableOptions.push(option);
        setOpenNewOptionPopup(false);
    }
    const updateBoardTitle = (newTitle: string) => {
        setTitle(newTitle);
        saveBoard({
            title: newTitle,
            data: data,
            id: id,
            availableOptions: availableOptions
        });
    }

    const handleOptionUpdate = (option: Option) => {

        if(modifyingOption === null) return;

        availableOptions[availableOptions.indexOf(modifyingOption)] = option;

        const board = getBoardById(id);
        if(board === null) return;

        board.data = board.data.map((boardData) => {

            for (const [dayInYear, boardDay] of boardData.days) {
                if(boardDay == null){ continue; }
                for(const selectedOption of boardDay.selectedOptions)
                {
                    if(selectedOption.serialize() === modifyingOption.serialize()){
                        boardDay.selectedOptions.delete(selectedOption);
                        boardDay.selectedOptions.add(option);
                        break;
                    }
                }
                boardData.days.set(dayInYear, boardDay);
            }

            return boardData;
        });


        saveBoard({
            title: title,
            data: board.data,
            id: id,
            availableOptions: availableOptions
        });

        setModifyingOption(null);

        if (reloadBoard) {
            reloadBoard();
        }
    }

    const handleRemoveOption = (modifyingOption: Option) => {

        if(modifyingOption === null) return;

        const newAvailableOptions = availableOptions.filter((currentOption) => currentOption.serialize() !== modifyingOption.serialize());

        const board = getBoardById(id);
        if(board === null) return;

        board.data = board.data.map((boardData) => {

            for (const [dayInYear, boardDay] of boardData.days) {
                if(boardDay == null){ continue; }
                for(const selectedOption of boardDay.selectedOptions)
                {
                    if(selectedOption.serialize() === modifyingOption.serialize()){
                        boardDay.selectedOptions.delete(selectedOption);
                        break;
                    }
                }
                boardData.days.set(dayInYear, boardDay);
            }

            return boardData;
        });


        saveBoard({
            title: title,
            data: board.data,
            id: id,
            availableOptions: newAvailableOptions
        });

        setModifyingOption(null);

        if (reloadBoard) {
            reloadBoard();
        }
    }

    const updateDay = (day: Day | null) => {

        let yearExists = false;
        const modifiedData = data.map((boardData) => {
            if(boardData.year !== selectedYear) return boardData;
            yearExists = true;
            boardData.days.set(getDayNumberOfYear(selectedDay?.day ?? 1, selectedDay?.month ?? 1), day);

            return boardData;
        })

        if(!yearExists)
        {
            data.push({
                year: selectedYear,
                days: new Map<number,Day | null>([
                    [getDayNumberOfYear(selectedDay?.day ?? 1, selectedDay?.month ?? 1), day]
                ])
            });
        }

        saveBoard({
            title: title,
            data: modifiedData,
            id: id,
            availableOptions: availableOptions
        });

    }

    const getDayNumberOfYear = (day: number, month: number): number => {
        const date = new Date(selectedYear, month - 1, day).setHours(23,59,59,999).valueOf();
        const startOfYear = new Date(selectedYear, 0, 1).setHours(0,0,0,0).valueOf();
        // @ts-ignore
        const diff = date - startOfYear;
        const oneDay = 86400000; //1000 * 60 * 60 * 24;
        const dayNumber = Math.ceil(diff / oneDay);
        console.log(`Resolved ${day}-${month} to be day number ${dayNumber}`);
        return dayNumber;
    }

    const handleDayPopup = (day: number, month: number) => {

        const thisYearsData = data.find((boardData) => boardData.year === selectedYear) ?? {
            year: selectedYear,
            days: new Map<number, Day | null>()
        };

        const dayObject = thisYearsData.days.get(getDayNumberOfYear(day,month));

        setSelectedDay({
            day: day,
            month: month,
            dayObject: dayObject ?? new Day(new Set<Option>(), "")
        });
    }

    const handleBoardDelete = () => {

        deleteBoardById(id);
        enqueueSnackbar("Board deleted", { variant: "success" });
        if (reloadBoard) {
            reloadBoard();
        }
    }

    const getDateFromDayNumber = (dayOfYear: number, year: number) => {
        // Start of the year
        const startOfYear = new Date(year, 0, 1); // January 1st
        // Add the day number as an offset (subtract 1 because January 1 is day 1)
        const targetDate = new Date(startOfYear.setDate(dayOfYear));

        // Extract the month and day
        const month = targetDate.getMonth() + 1; // Months are 0-based, so add 1
        const day = targetDate.getDate();

        return { day, month };
    }

    const getAbsoluteDayNumber = (month: number, day: number, year: number): number => {
        // Create a date object for the given year
        const startOfYear = new Date(year, 0, 1); // January 1st
        const targetDate = new Date(year, month - 1, day); // Adjust for zero-based months

        // Calculate the difference in milliseconds
        const diffInMs = targetDate.getTime() - startOfYear.getTime();

        // Convert milliseconds to days
        return Math.floor(diffInMs / (1000 * 60 * 60 * 24)) + 1;
    }

    const getMonthFirstLetter = (monthNumber: number) => {
        // Ensure the month number is valid (1 to 12)
        if (monthNumber < 1 || monthNumber > 12) {
            throw new Error('Month number must be between 1 and 12.');
        }

        // Get the user's locale from the browser
        const userLocale = navigator.language || 'en-US';

        // Create a Date object for the specified month
        const date = new Date(selectedYear, monthNumber - 1); // monthNumber - 1 since JS months are 0-indexed

        // Get the full month name using toLocaleString
        const monthName = date.toLocaleString(userLocale, { month: 'long' });

        // Return the first letter of the month name
        return monthName.charAt(0);
    }

    const isToday = (day: number, month: number, year: number) => {
        const today = new Date();
        return today.getDate() === day && (today.getMonth() + 1) === month && today.getFullYear() === year;
    }

    const [components, setComponents] = useState<{ [x: number]: any[] }>({1:[],2:[],3:[],4:[],5:[],6:[],7:[],8:[],9:[],10:[],11:[],12:[]});

    useEffect(() => {
        const thisYearsData = data.find((boardData) => boardData.year === selectedYear) ?? {
            year: selectedYear,
            days: new Map<number, Day | null>()
        };

        for(let dayNumber = 1; dayNumber <= getAbsoluteDayNumber(12,31,selectedYear); dayNumber++){
            if(!thisYearsData.days.has(dayNumber))
            {
                thisYearsData.days.set(dayNumber, null);
            }
        }

        const internalDayComponents: any = {};

        for(let dayNumber = 1; dayNumber <= getAbsoluteDayNumber(12,31,selectedYear); dayNumber++){
            const localDay = thisYearsData.days.get(dayNumber);

            const {day, month} = getDateFromDayNumber(dayNumber, selectedYear)

            if(day === 1){
                internalDayComponents[month] = [];
            }

            const colors: string[] = [];

            localDay?.selectedOptions.forEach((selectedOption) => {
                colors.push(selectedOption.color);
            })

            let linearGradient;
            if(colors.length > 1) {
                linearGradient = `linear-gradient(45deg, ${colors.join(', ')})`;
            }
            else if(colors.length === 1){
                linearGradient = colors[0];
            }
            else{
                linearGradient = '#d5d5d5';
            }

            const currentDayIsToday = isToday(day, month, selectedYear);

            if(day === 1) {
                internalDayComponents[month].push(
                    <Box key={`board-${id}-${selectedYear}-calendar-${dayNumber}-monthLetter`} sx={{textAlign: 'right', pr: '6px'}}>
                        <Typography>{getMonthFirstLetter(month)}</Typography>
                    </Box>
                )
            }
            if(month === 1)
            {
                internalDayComponents[month].push (
                    <Box key={`board-${id}-${selectedYear}-calendar-${dayNumber}`} sx={{display: 'flex'}}>
                        <Box sx={{
                            width: '24px',
                            height: '18px',
                            margin: 0,
                            padding: 0,
                            boxSizing: 'border-box',
                        }}>
                            <Typography sx={{fontSize: '14px'}}>{day}</Typography>
                        </Box>
                        <Box sx={{
                            borderRadius: '6px',
                            width: '18px',
                            height: '18px',
                            margin: 0,
                            padding: 0,
                            fontSize: '0px',
                            background: linearGradient,
                            marginBottom: '1px',
                            marginRight: '4px',
                            boxSizing: 'border-box',
                            border: currentDayIsToday ? '2px solid black' : undefined
                        }}
                            onClick={() => handleDayPopup(day, month)}
                        >

                        </Box>
                    </Box>
                )
            }
            else {
                internalDayComponents[month].push(
                    <Box key={`board-${id}-${selectedYear}-calendar-${dayNumber}`} sx={{
                        borderRadius: '6px',
                        width: '18px',
                        height: '18px',
                        margin: 0,
                        padding: 0,
                        fontSize: '0px',
                        background: linearGradient,
                        marginBottom: '1px',
                        marginRight: '4px',
                        boxSizing: 'border-box',
                        border: currentDayIsToday ? '2px solid black' : undefined
                    }}
                         onClick={() => handleDayPopup(day, month)}
                    >

                    </Box>
                )
            }
        }

        setComponents(internalDayComponents);

    }, [selectedYear, data, selectedDay]);

    const sxStyle = (components: any[]): CSSProperties => {
        return {
            // height: components.length * 8,
            display: 'flex',
            // justifyContent: 'space-between',
            flexDirection: 'column'
        }
    }

    const looper: number[] = [0,1,2,3,4,5,6,7,8,9,10,11,12];

    const allComponents = useMemo(() => {

        return looper.map((monthNumber) => {
            return (
                <Box key={`board-${id}-generated-month-${monthNumber}`} className={`month-${monthNumber}`} sx={sxStyle(components[monthNumber])}>
                    {
                        components[monthNumber]
                    }
                </Box>
            )
        });

    }, [components]);

    const getItems = useCallback(() => {
        const thisYearsData = data.find((boardData) => boardData.year === selectedYear) ?? {
            year: selectedYear,
            days: new Map<number, Day | null>()
        };

        const selectedOptionCount: any = {};
        availableOptions.forEach(availableOption => {
            selectedOptionCount[availableOption.name] = { color: availableOption.color, amount: 0};
        });

        for(let dayNumber = 1; dayNumber <= getAbsoluteDayNumber(12,31,selectedYear); dayNumber++) {
            const localDay = thisYearsData.days.get(dayNumber);
            if(localDay == null || localDay == undefined) continue;

            localDay.selectedOptions.forEach(selectedValue => {
                selectedOptionCount[selectedValue.name]['amount'] += 1;
            })
        }

        const values = Object.keys(selectedOptionCount).map((key) => {
            return {asset: key, color: selectedOptionCount[key]['color'], amount: selectedOptionCount[key]['amount']};
        });

        return {
            total: values.map((item) => item.amount).reduce((partialSum, a) => partialSum + a, 0),
            values: values
        }
            //{ asset: "Stocks", amount: 60000 }
    }, [availableOptions, data, selectedYear]);

    useEffect(() => {
        const items = getItems();
    }, []);

    return (
        <Paper
            sx={{
                height: '300px',
                p: 1,
                ...options?.sx
            }}
            onClick={onClick ?? undefined}
        >
            <Box className={'heading'} sx={{
                p: 2,
            }}>
                <Button
                    size="large"
                    variant="text"
                    onClick={() => { navigate(`/`) }}
                    sx={{color: '#afafaf'}}
                >
                    <ArrowBackIosIcon />
                </Button>
                <Input  sx={{pt:1, color: "#606060", mb: 0.5}} value={title} onChange={(e) => updateBoardTitle(e.target.value)} disableUnderline={true} />

                <Box sx={{position: 'absolute', top: '4vh', right: '10vw', color: "#8D8D8D"}} onClick={() => setShowBoardDeleteConfirmationDialog(true)}>
                    <DeleteIcon />
                </Box>

            </Box>
            <Box sx={{display: 'flex'}}>
                <Box sx={{display: 'flex', px: 2}}>

                    {
                        allComponents
                    }
                </Box>
                <Box sx={{ marginX: 'auto' }}>
                    {
                        availableOptions.map((availableOption) => {

                            return (
                                <Box key={`board-${id}-available-option-${availableOption.name}`} sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1}}>
                                    <Box
                                        sx={{
                                            width: '28px',
                                            height: '28px',
                                            borderRadius: '6px',
                                            background: availableOption.color,
                                            mb: 1,
                                        }}
                                        onClick={() => setModifyingOption(availableOption) }
                                    >
                                    </Box>
                                    <Typography sx={{fontSize: 14, textAlign: 'center'}}>{availableOption.name}</Typography>
                                </Box>
                            );

                        })
                    }
                    <Box
                        sx={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '6px',
                            background: (theme) => theme.palette.grey.A200,
                            mb: 1,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            m: 'auto',
                            mt: 2,
                        }}
                        onClick={() => setOpenNewOptionPopup(true) }
                    >
                        <AddIcon />
                    </Box>
                </Box>
            </Box>

            <Box sx={{mt: 8}} >

                <div style={{ height: 500 }}>
                    <AgCharts options={{
                        data: getItems().values,
                        title: {
                            text: "Overview",
                        },
                        series: [
                            {
                                type: "pie",
                                angleKey: "amount",
                                legendItemKey: "asset",
                                fills: getItems().values.map(item => item.color),
                            },
                        ],
                    }} />

                    <Box>
                        {
                            getItems().values.map((item) => {
                                return <Typography fontSize={14}>{item.asset}: {item.amount} ({Math.round((item.amount * 100 / getItems().total) * 100) / 100}%)</Typography>
                            })
                        }
                    </Box>
                </div>


            </Box>

            {
                selectedDay &&
                <ClickAwayListener onClickAway={() => setSelectedDay(null)}>
                    <DayPopup
                        open={selectedDay != null}
                        day={selectedDay.day ?? 1}
                        month={selectedDay.month ?? 1}
                        dayModel={selectedDay.dayObject}
                        handleClose={()=> setSelectedDay(null) }
                        availableOptions={availableOptions}
                        handleDayModelChange={(dayModel: Day) => {
                            setSelectedDay((currentSelectedDay) => {
                                if(currentSelectedDay === null){
                                    return null;
                                }
                                updateDay(dayModel);

                                const newDayObject = {
                                    day: currentSelectedDay.day ?? 1,
                                    month: currentSelectedDay.month ?? 1,
                                    dayObject: dayModel
                                };

                                return newDayObject;
                            });
                        }}
                    />
                </ClickAwayListener>

            }

            {
                openNewOptionPopup &&
                <ClickAwayListener onClickAway={() => setOpenNewOptionPopup(false)} >
                    <AddOptionPopup handleClose={() => setOpenNewOptionPopup(false)} open={openNewOptionPopup} handleSave={handleOptionAdd} />
                </ClickAwayListener>
            }

            {
                modifyingOption &&
                <ClickAwayListener onClickAway={() => setModifyingOption(null)} >
                    <EditOptionPopup
                        handleClose={() => setModifyingOption(null)}
                        open={true}
                        handleUpdate={handleOptionUpdate}
                        initialState={{ option: modifyingOption }}
                        handleRemove={() => handleRemoveOption(modifyingOption)}
                    />
                </ClickAwayListener>
            }

            {
                showBoardDeleteConfirmationDialog &&
                <ClickAwayListener onClickAway={() => setShowBoardDeleteConfirmationDialog(false)} >
                    <DeleteBoardPopup

                        name={title}
                        handleClose={() => setShowBoardDeleteConfirmationDialog(false)}
                        handleDelete={() => handleBoardDelete()}
                        open={true}
                    />
                </ClickAwayListener>
            }

        </Paper>
    )

}